import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import simpleProjects from '../assets/projects';
import detailedProjects from '../Projects/projects';
import { ArrowLeft, ArrowUpRight, Server, Shield, Activity, BarChart, CheckCircle2, Cpu } from 'lucide-react';
import { motion } from 'framer-motion';

const ArchitectureFlow = ({ steps }) => {
  if (!steps || steps.length === 0) return null;
  return (
    <div className="my-8 bg-slate-900/60 border border-white/10 p-6 rounded-2xl shadow-inner">
      <h4 className="text-md font-bold text-cyan-400 mb-6 text-center tracking-wider uppercase">Data Flow & System Architecture</h4>
      <div className="flex flex-col md:flex-row flex-wrap justify-center items-center gap-3 md:gap-4">
        {steps.map((step, idx) => (
          <React.Fragment key={idx}>
            <motion.div 
              whileHover={{ y: -2 }}
              className="flex items-center justify-center bg-white/5 border border-white/10 px-4 py-3 rounded-xl shadow-md min-w-[140px] text-center hover:border-cyan-400 hover:shadow-cyan-500/10 transition-all duration-300"
            >
              <span className="text-xs md:text-sm font-semibold text-gray-200">{step}</span>
            </motion.div>
            {idx < steps.length - 1 && (
              <span className="text-cyan-400 font-bold rotate-90 md:rotate-0 text-xl animate-pulse">➔</span>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const ProjectDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    AOS.init({ duration: 700, once: true });
    window.scrollTo(0, 0);
  }, [slug]);

  // 1. Search in detailed projects (e.g. portfolio-website, stockpilot, etc.)
  let project = detailedProjects.find((p) => p.id === slug);
  let isDetailed = true;

  // 2. Fallback to simple projects array
  if (!project) {
    const allSimple = simpleProjects.flatMap((cat) =>
      cat.projects.map((p) => ({ ...p, category: cat.category }))
    );
    project = allSimple.find((p) => p.projectSlug === slug);
    isDetailed = false;
  }

  if (!project) {
    return (
      <div className="text-white text-center py-32">
        <h2 className="text-3xl font-bold mb-4">Project not found</h2>
        <button
          onClick={() => navigate(-1)}
          className="text-cyan-400 hover:text-cyan-300 underline"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <section className="text-white py-24 px-4 sm:px-6 md:px-10 lg:px-32 bg-gradient-to-br from-[#0f1f2e]/90 via-[#102e42]/90 to-[#0e1d2c]/90 min-h-screen">
      <div className="max-w-6xl mx-auto mb-8">
        <Link to="/#projects" className="text-cyan-300 flex items-center gap-2 hover:underline w-fit">
          <ArrowLeft size={16} /> Back to Projects
        </Link>
      </div>

      <div data-aos="fade-up" className="max-w-6xl mx-auto bg-white/5 backdrop-blur-xl rounded-3xl p-6 md:p-10 border border-white/10 shadow-2xl">
        {/* Header Title + Tagline */}
        <div className="border-b border-white/10 pb-6 mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-cyan-300 mb-3 bg-gradient-to-r from-cyan-400 to-indigo-400 bg-clip-text text-transparent">{project.title}</h2>
            <p className="text-gray-400 font-medium text-sm md:text-base">{project.tagline || "Flagship Software Implementation"}</p>
          </div>
          <div className="flex gap-3">
            {project.links?.live && project.links.live !== "#" && (
              <a
                href={project.links.live}
                target="_blank"
                rel="noreferrer"
                className="px-5 py-2.5 rounded-xl text-xs font-semibold bg-gradient-to-r from-cyan-500 to-indigo-500 hover:from-cyan-400 hover:to-indigo-400 text-white flex items-center gap-1.5 shadow-lg transition duration-300"
              >
                Live Project <ArrowUpRight size={14} />
              </a>
            )}
            {project.links?.repo && (
              <a
                href={project.links.repo}
                target="_blank"
                rel="noreferrer"
                className="px-5 py-2.5 rounded-xl text-xs font-semibold bg-white/5 border border-white/10 hover:bg-white/10 text-white flex items-center gap-1.5 transition duration-300"
              >
                GitHub <ArrowUpRight size={14} />
              </a>
            )}
          </div>
        </div>

        {/* Project Cover Banner */}
        {project.cover && (
          <div className="relative rounded-3xl overflow-hidden mb-10 border border-white/10 max-h-[380px] shadow-2xl">
            <img 
              src={project.cover} 
              alt={`${project.title} Cover`}
              className="w-full h-full object-cover"
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
          </div>
        )}

        {/* Detailed Tabs Layout */}
        {isDetailed ? (
          <div>
            {/* Tabs Buttons */}
            <div className="flex border-b border-white/10 mb-8 overflow-x-auto gap-2 no-scrollbar">
              {[
                { id: "overview", label: "Overview", icon: <Cpu className="w-4 h-4" /> },
                { id: "tech", label: "Architecture & Stack", icon: <Server className="w-4 h-4" /> },
                { id: "security", label: "Security & Scale", icon: <Shield className="w-4 h-4" /> },
                { id: "testing", label: "Quality & Logs", icon: <Activity className="w-4 h-4" /> }
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className={`px-5 py-3 border-b-2 font-semibold text-xs md:text-sm transition-all duration-300 whitespace-nowrap flex items-center gap-1.5 cursor-pointer ${
                    activeTab === t.id
                      ? "border-cyan-400 text-cyan-300 bg-white/5"
                      : "border-transparent text-gray-400 hover:text-gray-200"
                  }`}
                >
                  {t.icon}
                  {t.label}
                </button>
              ))}
            </div>

            {/* Tab Contents */}
            <div>
              {activeTab === "overview" && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Project Brief</h3>
                    <p className="text-gray-300 leading-relaxed text-sm md:text-base whitespace-pre-line">{project.overview}</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mt-8">
                    <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
                      <h4 className="text-lg font-bold text-red-400 mb-2 flex items-center gap-2">
                        <span>⚠️</span> The Problem
                      </h4>
                      <p className="text-gray-300 text-sm leading-relaxed">{project.problem}</p>
                    </div>
                    <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
                      <h4 className="text-lg font-bold text-emerald-400 mb-2 flex items-center gap-2">
                        <span>✅</span> The Solution
                      </h4>
                      <p className="text-gray-300 text-sm leading-relaxed">{project.solution}</p>
                    </div>
                  </div>

                  {/* Impact Stats Card */}
                  <div className="mt-8 bg-slate-900/60 border border-cyan-400/20 p-6 rounded-2xl">
                    <h4 className="text-md font-bold text-cyan-300 mb-4 uppercase tracking-wider">Project Business Impact Metrics</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                      <div className="text-center">
                        <div className="text-3xl font-extrabold text-cyan-400">{project.stats.stars || "Active"}</div>
                        <div className="text-xs text-gray-400 mt-1 uppercase font-semibold">Scale Status</div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-extrabold text-indigo-400">{project.stats.users || "Production"}</div>
                        <div className="text-xs text-gray-400 mt-1 uppercase font-semibold">User Traction</div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-extrabold text-emerald-400">{project.stats.performance || "100% Opt"}</div>
                        <div className="text-xs text-gray-400 mt-1 uppercase font-semibold">Efficiency Gain</div>
                      </div>
                    </div>
                  </div>

                  {/* Key Features List */}
                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-white mb-4">Core Implementation Features</h3>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {project.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm text-gray-300">
                          <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {activeTab === "tech" && (
                <div className="space-y-6">
                  {/* Architecture Diagram */}
                  <ArchitectureFlow steps={project.architecture} />

                  <div className="grid md:grid-cols-3 gap-6 mt-8">
                    <div className="bg-white/5 p-5 rounded-xl border border-white/10">
                      <h4 className="text-sm font-bold text-cyan-400 mb-3 uppercase tracking-wider">Frontend Stack</h4>
                      <div className="flex flex-wrap gap-2">
                        {project.techStack.frontend?.map((t, i) => (
                          <span key={i} className="text-xs px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-gray-200">{t}</span>
                        ))}
                      </div>
                    </div>
                    <div className="bg-white/5 p-5 rounded-xl border border-white/10">
                      <h4 className="text-sm font-bold text-emerald-400 mb-3 uppercase tracking-wider">Backend & AI</h4>
                      <div className="flex flex-wrap gap-2">
                        {project.techStack.backend?.map((t, i) => (
                          <span key={i} className="text-xs px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-gray-200">{t}</span>
                        ))}
                      </div>
                    </div>
                    <div className="bg-white/5 p-5 rounded-xl border border-white/10">
                      <h4 className="text-sm font-bold text-indigo-400 mb-3 uppercase tracking-wider">Infrastructure</h4>
                      <div className="flex flex-wrap gap-2">
                        {project.techStack.infra?.map((t, i) => (
                          <span key={i} className="text-xs px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-gray-200">{t}</span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Design Decisions & Importance</h3>
                    <p className="text-gray-300 text-sm md:text-base leading-relaxed">{project.importance}</p>
                  </div>
                </div>
              )}

              {activeTab === "security" && (
                <div className="space-y-6">
                  <div className="bg-slate-900/60 p-6 rounded-2xl border border-cyan-400/10">
                    <h3 className="text-lg font-bold text-cyan-300 mb-4 flex items-center gap-2">
                      <Shield className="w-5 h-5" /> Production Security Actions
                    </h3>
                    <ul className="space-y-3">
                      {project.securityMeasures?.map((sec, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm text-gray-300">
                          <span className="text-cyan-400 font-bold shrink-0">🔒</span>
                          <span>{sec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-slate-900/60 p-6 rounded-2xl border border-emerald-400/10">
                    <h3 className="text-lg font-bold text-emerald-300 mb-4 flex items-center gap-2">
                      <BarChart className="w-5 h-5" /> Latency & Performance Tuning
                    </h3>
                    <ul className="space-y-3">
                      {project.performanceOptimizations?.map((perf, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm text-gray-300">
                          <span className="text-emerald-400 font-bold shrink-0">⚡</span>
                          <span>{perf}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {activeTab === "testing" && (
                <div className="space-y-6">
                  <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
                    <h3 className="text-xl font-bold text-white mb-4">Quality Assurance Strategy</h3>
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-xs font-bold text-cyan-400 uppercase tracking-wide">Client/UI Testing</h4>
                        <p className="text-gray-300 text-sm mt-1">{project.testing.frontend}</p>
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-emerald-400 uppercase tracking-wide">Server/API Integration</h4>
                        <p className="text-gray-300 text-sm mt-1">{project.testing.backend}</p>
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-indigo-400 uppercase tracking-wide">End-to-End Coverage</h4>
                        <p className="text-gray-300 text-sm mt-1">{project.testing.e2e}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
                    <h3 className="text-xl font-bold text-white mb-4">Observability & Health Checks</h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-xs font-bold text-cyan-400 uppercase tracking-wide">Error Reporting</h4>
                        <p className="text-gray-300 text-sm mt-1">{project.observability.errors}</p>
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-emerald-400 uppercase tracking-wide">Structured Logging</h4>
                        <p className="text-gray-300 text-sm mt-1">{project.observability.logs}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Simple Fallback Layout */
          <div>
            <p className="text-gray-300 text-base leading-relaxed mb-8">{project.description}</p>
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-cyan-400 mb-3">Technologies Utilized:</h4>
              <div className="flex flex-wrap gap-2">
                {project.stack?.map((tech, idx) => (
                  <span
                    key={idx}
                    className="bg-white/10 text-xs px-3 py-1.5 rounded-full text-cyan-300 border border-white/20 font-medium"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

    </section>
  );
};

export default ProjectDetail;
